import React from 'react';
import ReviewersCard from './ReviewersCard';
import SidebarInfoCard from './SidebarInfoCard';
import {
  GxpAddressObject,
  GxpLimitsApplied,
  GxpProductDetails,
  GxpReferenceCard,
  GxpReviewer,
  GxpShipmentDetails,
} from './types';

const formatAddress = (
  addr?: string | GxpAddressObject | null
): React.ReactNode => {
  if (!addr) return '--';

  if (typeof addr === 'string') return addr;

  const name = addr?.name?.trim();
  const address = addr?.address?.trim();
  const timezone = addr?.timezone?.trim();

  if (!name && !address) return '--';

  return (
    <>
      {name}
      {name && address && <br />}
      {address}
      {timezone && <br />}
      {timezone}
    </>
  );
};
import { Link } from 'react-router-dom';

interface GxpReportSidebarProps {
  isAutoPublish?: boolean;
  reviewers?: GxpReviewer;
  limitsApplied?: GxpLimitsApplied;
  shipmentDetails?: GxpShipmentDetails;
  products?: GxpProductDetails[];
  references?: GxpReferenceCard[];
}

const GxpReportSidebar: React.FC<GxpReportSidebarProps> = ({
  isAutoPublish,
  reviewers,
  limitsApplied,
  shipmentDetails,
  products,
  references,
}) => {
  return (
    <div className='gxp-sidebar'>
      <ReviewersCard isAutoPublish={isAutoPublish} reviewers={reviewers} />

      {/* backend check if we should show or not  */}
      {limitsApplied?.present && (
        <SidebarInfoCard
          title='Limits applied'
          rows={[
            {
              key: 'temp-range',
              label: 'Temperature range',
              value: limitsApplied?.temperature_range_label,
            },
            {
              key: 'mkt',
              label: 'MKT limit',
              value: limitsApplied?.mkt_limit_celsius
                ? 'Not more than ' +
                  limitsApplied?.mkt_limit_celsius +
                  limitsApplied?.temperature_unit
                : '--',
            },
            {
              key: 'activation',
              label: 'Activation energy',
              value: limitsApplied?.activation_energy_kj_mol
                ? limitsApplied?.activation_energy_kj_mol + ' kJ/mol'
                : '--',
            },
            ...(limitsApplied?.allowable_ranges_available
              ? [
                  {
                    key: 'allowable',
                    label: 'Allowable ranges',
                    value: (
                      <Link
                        to='/settings/product'
                        state={{ productId: limitsApplied?.product_id }}
                        className='gxp-sidebar-card__link'
                      >
                        View
                      </Link>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      <SidebarInfoCard
        title='Shipment details'
        rows={[
          {
            key: 'origin',
            label: 'Origin',
            value: <div>{formatAddress(shipmentDetails?.origin)}</div>,
          },
          {
            key: 'destination',
            label: 'Destination',
            value: <div>{formatAddress(shipmentDetails?.destination)}</div>,
          },
          {
            key: 'pickup',
            label: 'Pickup (POP)',
            value: shipmentDetails?.picked_up_at,
          },
          {
            key: 'delivery',
            label: 'Delivery (POD)',
            value: shipmentDetails?.delivered_at,
          },
          ...(shipmentDetails?.picked_up_at_shipper_local
            ? [
                {
                  key: 'pickup-shipper-local',
                  label: 'Pickup (shipper local)',
                  value: shipmentDetails.picked_up_at_shipper_local,
                },
              ]
            : []),
          ...(shipmentDetails?.delivered_at_consignee_local
            ? [
                {
                  key: 'delivery-consignee-local',
                  label: 'Delivery (consignee local)',
                  value: shipmentDetails.delivered_at_consignee_local,
                },
              ]
            : []),
        ]}
      />

      {products?.length > 0 &&
        products.map((product, index) => (
          <SidebarInfoCard
            key={product?.product_id ?? index}
            title='Products'
            rows={[
              { key: 'name', label: 'Name', value: product?.name ?? '--' },
              {
                key: 'qty',
                label: 'Quantity',
                value: product?.quantity ?? 'N/A',
              },
              {
                key: 'type',
                label: 'Product type',
                value: product?.product_type,
              },
              {
                key: 'weight',
                label: 'Total weight',
                value: product?.total_weight ?? 'N/A',
              },
              {
                key: 'dims',
                label: 'Dimensions',
                value: product?.dimensions ?? 'N/A',
              },
            ]}
          />
        ))}

      {/* References supports card */}
      {references?.length > 0 && (
        <SidebarInfoCard
          title={'References'}
          rows={references?.map((row, idx) => ({
            key: `${idx}`,
            label: row.label,
            value: row.value,
          }))}
        />
      )}
    </div>
  );
};

export default GxpReportSidebar;
